import { useDecryptAxeptaData } from '@massilia/queries'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { DepositConfirmationFailed } from '@/components/deposit-payment/deposit-confirmation-failed'
import { DepositConfirmationSuccess } from '@/components/deposit-payment/deposit-confirmation-success'

// Axepta redirects include various query params (case-insensitive).
// For failure redirects, encrypted Data+Len params are included.
// For success redirects, plain params like UserData are included.
const depositConfirmationSearchSchema = z.object({
  Data: z.string().optional(),
  data: z.string().optional(),
  Len: z.coerce.string().optional(),
  len: z.coerce.string().optional(),
  UserData: z.string().optional(),
  userData: z.string().optional(),
})

export const Route = createFileRoute('/_blue_header/payer-acompte/confirmation/$status')({
  component: RouteComponent,
  validateSearch: depositConfirmationSearchSchema,
})

/**
 * Parse UserData encoded as "deposit|reference|amountCents".
 * Axepta returns UserData as-is on redirect.
 */
function parseDepositUserData(userData: string | undefined) {
  if (!userData) return { amountCents: undefined, reference: undefined }

  const parts = userData.split('|')
  if (parts[0] !== 'deposit' || parts.length < 3) {
    return { amountCents: undefined, reference: undefined }
  }

  const reference = parts[1]
  const amountCents = Number(parts[2])

  return {
    amountCents: Number.isFinite(amountCents) ? amountCents : undefined,
    reference: reference || undefined,
  }
}

function RouteComponent() {
  const { status } = Route.useParams()
  const searchParams = Route.useSearch()

  // Handle encrypted Axepta data (failure redirects)
  const axeptaData = searchParams.Data ?? searchParams.data
  const axeptaLen = searchParams.Len ?? searchParams.len
  const { data: decryptedAxeptaDetails } = useDecryptAxeptaData(axeptaData, axeptaLen)

  // Resolve UserData from plain params or decrypted data
  const rawUserData =
    searchParams.UserData ?? searchParams.userData ?? decryptedAxeptaDetails?.UserData

  const { amountCents, reference } = parseDepositUserData(rawUserData)

  if (status === 'success') {
    return <DepositConfirmationSuccess amountCents={amountCents} reference={reference} />
  }

  return <DepositConfirmationFailed />
}
